import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useRef, useState } from "react";
import { normalisePhone } from "@/utils";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { Result } from "react-spreadsheet-import/types/types";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useRegisterProviderClientMutation } from "@/redux/apis/auth/authApi";
import { RegisterProviderClientParams } from "@/redux/apis/auth/types";
import { ClientsImportResultModal } from "./ClientsImportResultModal";
import { importFields } from "./fixtures";
import { UploadClientListModal } from "./modals/UploadClientListModal";

type ImportRows = Result<string>["validData"];

interface ClientsImportPanelProps {
  open: boolean;
  onClose: () => void;
  refetchClients: () => void;
}

export const ClientsImportPanel = ({ open, onClose, refetchClients }: ClientsImportPanelProps) => {
  const operator = useOperator();
  const [registerProviderClient] = useRegisterProviderClientMutation();
  const [errors, setErrors] = useState<Array<string>>([]);
  const [importCount, setImportCount] = useState<number>(0);
  const [openResult, setOpenResult] = useState(false);
  const [pendingRows, setPendingRows] = useState<ImportRows>([]);
  const [isUploading, setIsUploading] = useState(false);
  const resolveSubmitRef = useRef<(() => void) | null>(null);
  const keepWizardOpenRef = useRef(false);

  const getAccountType = (type?: string) => {
    const typeValue = type?.toLowerCase();

    switch (typeValue) {
      case "client":
        return "passenger";
      case "executive assistant":
        return "executive_assistant";
      default:
        return undefined;
    }
  };

  /**
   * Held open until the operator answers the confirmation. The wizard keeps
   * rendering behind it (see UploadClientListModal), so cancelling returns them
   * to their parsed file rather than making them start over.
   */
  const handleSubmit = (data: Result<string>, _: File) =>
    new Promise<void>((resolve) => {
      setPendingRows(data.validData);
      resolveSubmitRef.current = resolve;
    });

  /** Resolving the submit promise is what clears the wizard's Continue spinner. */
  const settleSubmit = () => {
    resolveSubmitRef.current?.();
    resolveSubmitRef.current = null;
  };

  /**
   * The wizard closes itself once the submit promise resolves. Cancel resolves
   * too — only to clear that spinner — so swallow the close it triggers.
   */
  const handleWizardClose = () => {
    if (keepWizardOpenRef.current) {
      keepWizardOpenRef.current = false;
      return;
    }

    onClose();
  };

  const handleCancelUpload = () => {
    keepWizardOpenRef.current = true;
    setPendingRows([]);
    settleSubmit();
  };

  const handleConfirmUpload = async () => {
    const importErrors: Array<string> = [];
    let successCount = 0;
    if (!operator) return settleSubmit();

    setIsUploading(true);

    await Promise.all(
      pendingRows.map(async (data) => {
        const accountType = getAccountType(data.accountType as string);
        const metadataType = accountType || "passenger";

        const clientData = {
          providerId: operator.id,
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          mobile: data.contactNumber || "",
          provider_code: operator.providerCode,
          registered_device: "browser",
          company_name: data.companyName,
          operator_identifier: data.uniqueIdentifier,
          type: accountType,
          send_login_instructions: true,
          hear_about_us: "Other",
          account_metadata: {
            is_register_complete: true,
            provider_code: operator.providerCode,
            type: metadataType,
          },
        } as RegisterProviderClientParams;

        await registerProviderClient(clientData)
          .unwrap()
          .then(() => successCount++)
          .catch((e) => getErrorMessages(e).forEach((m) => importErrors.push(`${data.firstName} ${data.lastName} | ${m}`)));
      })
    );

    setIsUploading(false);
    setPendingRows([]);
    setImportCount(successCount);
    setErrors(importErrors);
    settleSubmit();
    setOpenResult(true);
    refetchClients();
  };

  return (
    <>
      <ReactSpreadsheetImport
        isOpen={open}
        onClose={handleWizardClose}
        onSubmit={handleSubmit}
        fields={importFields}
        allowInvalidSubmit={false}
        customTheme={{
          colors: {
            background: "white",
            rsi: {
              50: operator.branding.light,
              500: operator.branding.primary,
              900: operator.branding.dark,
            },
          },
        }}
        rowHook={(data, addError) => {
          let contactNumber = data.contactNumber as string;
          const country = operator.country as CountryCode;

          if (contactNumber) {
            // Same rule the bulk BOOKING importer applies, shared so a fix to one reaches both — this
            // panel is where it was worked out first. Conditions unchanged.
            if (isValidPhoneNumber(contactNumber, country)) contactNumber = normalisePhone(contactNumber, country);
            else if (!isValidPhoneNumber(contactNumber))
              addError("contactNumber", { message: "Contact Number is invalid", level: "error" });
          }

          return { ...data, contactNumber };
        }}
      />
      <UploadClientListModal
        open={pendingRows.length > 0}
        clientCount={pendingRows.length}
        isUploading={isUploading}
        onCancel={handleCancelUpload}
        onConfirm={handleConfirmUpload}
      />
      <ClientsImportResultModal importCount={importCount} errors={errors} open={openResult} onClose={() => setOpenResult(false)} />
    </>
  );
};
